import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Briefcase, Truck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { useProvince } from '../../contexts/ProvinceContext';

export const useLayoutData = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { siteName, logoUrl } = useSiteSettings();
  const { provinces, selectedProvince, setSelectedProvince, enterListingSection, exitListingSection, LISTING_SECTIONS } = useProvince();
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState({ unread_count: 0 });
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navbarSections, setNavbarSections] = useState([]);
  const [homeSections, setHomeSections] = useState([]);
  const [listingSections, setListingSections] = useState([]);
  const siteSettings = { logo_url: logoUrl, site_name: siteName };
  const prevPathRef = useRef(location.pathname);
  const [footerSettings, setFooterSettings] = useState({ address: '', email: '', phone: '', copyright_text: '' });
  const [bannerHeight, setBannerHeight] = useState(0);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [navbarVisible, setNavbarVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const navbarRef = useRef(null);

  const isDefaultLogo = !logoUrl || logoUrl === '/logo.png' || logoUrl === '/logo-dark.png';

  // Listing section enter/exit tracking
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;
    prevPathRef.current = currentPath;
    const isListingSec = (path) => {
      const sections = LISTING_SECTIONS || ['/property', '/vehicles', '/araclar', '/jobs', '/market', '/search', '/listing/', '/pazaryeri'];
      return sections.some(s => path.startsWith(s));
    };
    const isEventSec = (path) => path.startsWith('/events');
    const wasListing = isListingSec(prevPath) && !isEventSec(prevPath);
    const isListing = isListingSec(currentPath) && !isEventSec(currentPath);
    if (!wasListing && isListing && enterListingSection) enterListingSection();
    else if (wasListing && !isListing && exitListingSection) exitListingSection();
  }, [location.pathname, enterListingSection, exitListingSection, LISTING_SECTIONS]);

  // Initial listing section check
  useEffect(() => {
    const currentPath = location.pathname;
    const isListingSec = (path) => {
      const sections = LISTING_SECTIONS || ['/property', '/vehicles', '/araclar', '/jobs', '/market', '/search', '/listing/', '/pazaryeri'];
      return sections.some(s => path.startsWith(s));
    };
    if (isListingSec(currentPath) && !currentPath.startsWith('/events') && enterListingSection) enterListingSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterListingSection]);

  // Fetch footer settings
  useEffect(() => {
    axios.get(`${API}/footer-settings`).then(r => { if (r.data) setFooterSettings(r.data); }).catch(() => {});
  }, []);

  // Fetch navbar & home sections
  useEffect(() => {
    axios.get(`${API}/service-categories/navbar`).then(r => setNavbarSections(r.data.sections || [])).catch(() => {});
    axios.get(`${API}/service-categories/home-sections`).then(r => setHomeSections(r.data.sections || [])).catch(() => {});
    axios.get(`${API}/listing-sections`).then(r => setListingSections(r.data.sections?.filter(s => s.is_active && s.show_in_navbar) || [])).catch(() => {});
  }, []);

  // Auto-hide navbar on scroll — ref kullanımı with performanslı
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const lastY = lastScrollYRef.current;
      if (y < 50) setNavbarVisible(true);
      else if (y > lastY && y > 100) setNavbarVisible(false);
      else if (y < lastY) setNavbarVisible(true);
      lastScrollYRef.current = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const checkAuth = useCallback(async () => {
    try { const r = await axios.get(`${API}/auth/me`); setUser(r.data); }
    catch { setUser(null); }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try { const r = await axios.get(`${API}/notifications?limit=5`); setNotifications(r.data); }
    catch { /* non-critical */ }
  }, []);

  const fetchUnreadMessages = useCallback(async () => {
    try { const r = await axios.get(`${API}/messages/unread-count`); setUnreadMessages(r.data.count || 0); }
    catch { /* non-critical */ }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Iter401: Refresh the navbar/header user info when the user updates their
  // profile (name/avatar etc) on any page. ProfilePage dispatches this event
  // after a successful PUT /users/me/profile — we just re-pull /auth/me so
  // the header name, avatar and derived state stay in sync without a reload.
  useEffect(() => {
    const handler = () => { checkAuth(); };
    window.addEventListener('profile-updated', handler);
    return () => window.removeEventListener('profile-updated', handler);
  }, [checkAuth]);

  useEffect(() => {
    if (user) { fetchNotifications(); fetchUnreadMessages(); }
  }, [user, fetchNotifications, fetchUnreadMessages]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); setUser(null); navigate('/'); }
    catch (error) { toast.error(error.response?.data?.detail || 'An error occurred'); }
  };

  const handleLogin = () => navigate('/login');

  const staticNavItems = useMemo(() => [
    { href: '/', label: 'Home', icon: Home },
    { href: '/property', label: 'Property', icon: Home },
    { href: '/vehicles', label: 'Vehicles', icon: Home },
    { href: '/category/is-ilanlari', label: 'Jobs', icon: Briefcase },
    { href: '/services', label: 'Services', icon: Home },
    { href: '/shipping', label: 'Transport', icon: Truck },
    { href: '/events', label: 'Events', icon: Home },
    { href: '/marketplace', label: 'Marketplace', icon: Home },
  ], []);

  const navItems = useMemo(() => {
    const items = [{ href: '/', label: 'Home', icon: Home }];
    if (listingSections.length > 0) {
      const seen = new Set();
      listingSections.forEach(section => {
        if (!seen.has(section.slug) && section.is_active && section.show_in_navbar) {
          items.push({ href: `/${section.slug}`, label: section.name, icon: Home, section_type: 'listing' });
          seen.add(section.slug);
        }
      });
      return items;
    }
    return staticNavItems;
  }, [listingSections, staticNavItems]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleAddButtonClick = (e) => { e.preventDefault(); setShowAddMenu(true); };

  const handleAddOption = (option) => {
    setShowAddMenu(false);
    if (option === 'listing') navigate('/post-listing');
    else if (option === 'request') {
      if (location.pathname === '/') { navigate('/?openRequest=true'); window.dispatchEvent(new CustomEvent('openRequestModal')); }
      else navigate('/service-request/create');
    }
  };

  return {
    location, navigate, siteName, logoUrl, isDefaultLogo, siteSettings,
    provinces, selectedProvince, setSelectedProvince,
    user, notifications, unreadMessages,
    mobileMenuOpen, setMobileMenuOpen,
    navbarSections, homeSections, listingSections,
    footerSettings, bannerHeight, setBannerHeight,
    showAddMenu, setShowAddMenu,
    navbarVisible, navbarRef,
    navItems, isActive,
    handleLogout, handleLogin, handleAddButtonClick, handleAddOption,
  };
};
